import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  Tooltip,
} from '@material-ui/core';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import LinkIcon from '@material-ui/icons/Link';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { KeyboardDatePicker } from '@material-ui/pickers';
import moment from 'moment';
import { NetworkContext } from '../../../../context/NetworkContext';
import { SnackBarContext } from '../../../../context/SnackbarContext';

const STATUS_COLORS = {
  active: 'primary',
  expiring_soon: 'secondary',
  grace: 'default',
  blocked: 'default',
};

const STATUS_LABELS = {
  active: 'Active',
  expiring_soon: 'Expiring Soon',
  grace: 'Grace Period',
  blocked: 'Blocked',
};

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  statusBox: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    minHeight: 56,
  },
  statusLabel: {
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
  },
  statusChip: {
    alignSelf: 'flex-start',
    marginBottom: theme.spacing(0.5),
  },
  fieldRow: {
    marginBottom: theme.spacing(1),
  },
  actionsRow: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  actionButton: {
    minWidth: 120,
    height: 40,
  },
  extendField: {
    width: 140,
    marginBottom: 0,
  },
  paymentLinkField: {
    marginTop: theme.spacing(1),
  },
  linkHint: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  historyTable: {
    width: '100%',
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
    borderCollapse: 'collapse',
    '& th, & td': {
      padding: theme.spacing(1, 1.5, 1, 0),
      textAlign: 'left',
    },
  },
  historyRow: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  historySection: {
    marginTop: theme.spacing(3),
  },
  planPricesTitle: {
    marginTop: theme.spacing(1),
    fontWeight: 600,
  },
  planPricesHint: {
    marginBottom: theme.spacing(1.5),
    color: theme.palette.text.secondary,
  },
  planPriceRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  planPriceName: {
    fontWeight: 500,
    marginRight: theme.spacing(2),
  },
  planPriceField: {
    maxWidth: 220,
  },
}));

const SubscriptionDetails = ({ userid, viewtype }) => {
  const classes = useStyles();
  const { sendNetworkRequest } = React.useContext(NetworkContext);
  const snackbarCtx = React.useContext(SnackBarContext);

  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [plansLoaded, setPlansLoaded] = useState(false);
  const [payments, setPayments] = useState([]);
  const [extendDays, setExtendDays] = useState('30');
  const [offlineOpen, setOfflineOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);
  const [paymentLink, setPaymentLink] = useState('');
  const [offlineForm, setOfflineForm] = useState({ amount: '', notes: '', extend_days: '', coupon_code: '' });
  const [offlinePreview, setOfflinePreview] = useState(null);
  const [linkForm, setLinkForm] = useState({ coupon_code: '' });
  const [linkPreview, setLinkPreview] = useState(null);
  const [linkGenerateOpen, setLinkGenerateOpen] = useState(false);
  const [linkChargedAmount, setLinkChargedAmount] = useState(null);
  const [generatingInvoiceId, setGeneratingInvoiceId] = useState(null);

  async function loadSubscription() {
    const result = await sendNetworkRequest('/admin/getusersubscription', {}, { userid }, false);
    setSubscription(result);
    setOfflineForm(prev => ({ ...prev, amount: currentPlanUserPrice(result) }));
  }

  async function loadPayments() {
    const result = await sendNetworkRequest('/admin/listsubscriptionpayments', {}, { userid }, false);
    const rows = result?.payments || [];
    setPayments(rows.filter(p => p.status !== 'pending'));
  }

  async function loadPlans() {
    const result = await sendNetworkRequest('/admin/listsubscriptionplans', {}, { status: 'active' }, false);
    setPlans(result?.plans || []);
    setPlansLoaded(true);
  }

  useEffect(() => {
    if (userid) {
      loadSubscription();
      loadPayments();
      loadPlans();
    }
  }, [userid]);

  function showSnack(result) {
    snackbarCtx.setSnack({
      ...snackbarCtx,
      open: true,
      type: result.status || 'success',
      msg: result.message || 'Done',
      vertical: 'top',
    });
  }

  function mergePlanPrices(planList, existingPrices) {
    const priceMap = {};
    (existingPrices || []).forEach(row => {
      if (row && row.plan_id != null) {
        priceMap[Number(row.plan_id)] = row.amount;
      }
    });
    return (planList || []).map(plan => ({
      plan_id: plan.id,
      plan_name: plan.name,
      duration_days: plan.duration_days,
      amount: Object.prototype.hasOwnProperty.call(priceMap, Number(plan.id))
        ? priceMap[Number(plan.id)]
        : null,
    }));
  }

  function getPlanPriceRows(sub) {
    const source = sub || subscription;
    return mergePlanPrices(plans, source?.plan_prices);
  }

  function currentPlanUserPrice(sub) {
    const source = sub || subscription;
    if (!source) return '';
    const row = getPlanPriceRows(source).find(p => Number(p.plan_id) === Number(source.plan_id));
    if (row && row.amount != null && row.amount !== '') {
      return row.amount;
    }
    return source.amount || '';
  }

  function updatePlanPrice(planId, value) {
    const plan_prices = getPlanPriceRows().map(row => (
      Number(row.plan_id) === Number(planId)
        ? { ...row, amount: value === '' ? null : value }
        : row
    ));
    const extra = {};
    if (Number(subscription.plan_id) === Number(planId)) {
      extra.amount = value === '' ? '' : value;
    }
    setSubscription({ ...subscription, plan_prices, ...extra });
  }

  async function handleSave() {
    const payload = {
      userid,
      plan_id: subscription.plan_id || null,
      valid_from: subscription.valid_from
        ? moment(subscription.valid_from).format('YYYY-MM-DD')
        : null,
      valid_until: subscription.valid_until
        ? moment(subscription.valid_until).format('YYYY-MM-DD')
        : null,
      renewal_days: subscription.renewal_days,
      notes: subscription.notes,
      plan_prices: getPlanPriceRows().map(row => ({
        plan_id: row.plan_id,
        amount: row.amount,
      })),
    };
    const result = await sendNetworkRequest('/admin/saveusersubscription', {}, payload, false);
    showSnack(result);
    if (result.subscription) {
      setSubscription(result.subscription);
      setOfflineForm(prev => ({ ...prev, amount: currentPlanUserPrice(result.subscription) }));
    }
  }

  async function handleExtend() {
    const result = await sendNetworkRequest('/admin/extendsubscription', {}, {
      userid,
      extend_days: parseInt(extendDays, 10),
    }, false);
    showSnack(result);
    if (result.subscription) setSubscription(result.subscription);
    loadPayments();
  }

  async function applyCoupon({ code, amount, setter }) {
    const trimmed = (code || '').trim();
    if (!trimmed) {
      setter(null);
      showSnack({ status: 'error', message: 'Enter a coupon code' });
      return;
    }
    const result = await sendNetworkRequest('/admin/validatesubscriptioncoupon', {}, {
      userid,
      code: trimmed,
      amount: parseFloat(amount),
    }, false);
    if (result?.status === 'success') {
      setter(result);
      showSnack(result);
    } else {
      setter(null);
      showSnack({ status: 'error', message: result?.message || 'Invalid coupon' });
    }
  }

  async function handleOfflinePayment() {
    const payload = {
      userid,
      amount: parseFloat(offlineForm.amount),
      notes: offlineForm.notes,
    };
    if (offlineForm.extend_days) {
      payload.extend_days = parseInt(offlineForm.extend_days, 10);
    }
    if (offlineForm.coupon_code) {
      payload.coupon_code = offlineForm.coupon_code;
    }
    const result = await sendNetworkRequest('/admin/recordsubscriptionpayment', {}, payload, false);
    showSnack(result);
    if (result.status === 'success') {
      setOfflineOpen(false);
      setOfflinePreview(null);
      if (result.subscription) setSubscription(result.subscription);
      loadPayments();
      if (result.invoice?.invoice_url) {
        window.open(result.invoice.invoice_url, '_blank', 'noopener,noreferrer');
      }
    }
  }

  function downloadInvoicePdf(base64, fileName) {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'invoice.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  async function handleGenerateInvoice(payment) {
    if (!payment?.id || generatingInvoiceId) {
      return;
    }
    setGeneratingInvoiceId(payment.id);
    const result = await sendNetworkRequest('/admin/generatesubscriptioninvoice', {}, {
      payment_id: payment.id,
    }, false, true);
    setGeneratingInvoiceId(null);
    if (result?.status === 'success' && result.invoice_pdf) {
      downloadInvoicePdf(result.invoice_pdf, `${result.invoice_number || `INV-${payment.id}`}.pdf`);
      showSnack({ status: 'success', message: 'Invoice downloaded' });
      return;
    }
    if (result?.invoice_url) {
      window.open(result.invoice_url, '_blank', 'noopener,noreferrer');
      showSnack({ status: 'success', message: result.message || 'Invoice generated' });
      return;
    }
    showSnack({
      status: 'error',
      message: result?.message || 'Failed to generate invoice',
    });
  }

  function openPaymentLinkDialog() {
    setPaymentLink('');
    setLinkChargedAmount(null);
    setLinkPreview(null);
    setLinkForm({ coupon_code: '' });
    setLinkGenerateOpen(true);
  }

  async function handleGeneratePaymentLink() {
    setLinkLoading(true);
    setPaymentLink('');
    try {
      const payload = { userid };
      if (linkForm.coupon_code) {
        payload.coupon_code = linkForm.coupon_code;
      }
      const result = await sendNetworkRequest('/admin/createsubscriptionpaymentlink', {}, payload, false);
      if (result.short_url) {
        setPaymentLink(result.short_url);
        setLinkChargedAmount(result.amount);
        setLinkGenerateOpen(false);
        setLinkOpen(true);
        loadPayments();
        showSnack({ status: 'success', message: 'Payment link generated' });
      } else {
        showSnack({ status: 'error', message: result.message || 'Failed to generate link' });
      }
    } catch (error) {
      showSnack({ status: 'error', message: 'Failed to generate payment link' });
    } finally {
      setLinkLoading(false);
    }
  }

  function handleCopyLink() {
    if (!paymentLink) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(paymentLink).then(() => {
        showSnack({ status: 'success', message: 'Link copied to clipboard' });
      });
    }
  }

  function handleOpenLink() {
    if (paymentLink) {
      window.open(paymentLink, '_blank', 'noopener,noreferrer');
    }
  }

  if (!subscription) return null;

  const isReadOnly = viewtype == 2;
  const planOptions = [...plans];
  if (subscription.plan && !planOptions.some(p => p.id === subscription.plan.id)) {
    planOptions.unshift(subscription.plan);
  }
  const selectedPlan = planOptions.find(p => p.id === subscription.plan_id) || null;
  const planPriceRows = getPlanPriceRows();

  return (
    <Paper className={classes.root}>
      <Typography variant="h5" className={classes.sectionTitle}>
        Membership
      </Typography>
      <Grid container spacing={3} alignItems="flex-end" className={classes.fieldRow}>
        <Grid item xs={12} sm={6} md={3}>
          <Box className={classes.statusBox}>
            <Typography className={classes.statusLabel}>Status</Typography>
            <Chip
              className={classes.statusChip}
              label={STATUS_LABELS[subscription.status] || subscription.status}
              color={STATUS_COLORS[subscription.status] || 'default'}
            />
            {subscription.days_until_expiry != null && (
              <Typography variant="caption" color="textSecondary" display="block">
                {subscription.days_until_expiry >= 0
                  ? `${subscription.days_until_expiry} day(s) until expiry`
                  : `${Math.abs(subscription.days_until_expiry)} day(s) past expiry`}
              </Typography>
            )}
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            options={planOptions}
            getOptionLabel={option => option.name || ''}
            getOptionSelected={(option, value) => option.id === value.id}
            value={selectedPlan}
            disabled={isReadOnly}
            onChange={(_, plan) => {
              if (!plan) {
                setSubscription({ ...subscription, plan_id: null, plan: null });
                return;
              }
              const start = subscription.valid_from || moment().format('YYYY-MM-DD');
              const until = subscription.valid_until
                || moment(start).add(plan.duration_days, 'days').format('YYYY-MM-DD');
              const priced = getPlanPriceRows().find(p => Number(p.plan_id) === Number(plan.id));
              setSubscription({
                ...subscription,
                plan_id: plan.id,
                plan,
                amount: priced && priced.amount != null ? priced.amount : '',
                renewal_days: plan.duration_days,
                valid_from: start,
                valid_until: until,
              });
            }}
            renderInput={params => (
              <TextField {...params} label="Current membership" variant="outlined" margin="dense" fullWidth />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Renewal Days"
            type="number"
            variant="outlined"
            margin="dense"
            fullWidth
            disabled={isReadOnly}
            value={subscription.renewal_days || ''}
            onChange={e => setSubscription({ ...subscription, renewal_days: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KeyboardDatePicker
            disableToolbar
            variant="inline"
            format="DD/MM/YYYY"
            autoOk
            inputVariant="outlined"
            margin="dense"
            label="Start date"
            fullWidth
            disabled
            value={subscription.valid_from ? moment(subscription.valid_from) : null}
            onChange={date => setSubscription({
              ...subscription,
              valid_from: date ? date.format('YYYY-MM-DD') : null,
            })}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KeyboardDatePicker
            disableToolbar
            variant="inline"
            format="DD/MM/YYYY"
            autoOk
            inputVariant="outlined"
            margin="dense"
            label="Expiry date"
            fullWidth
            disabled={isReadOnly}
            value={subscription.valid_until ? moment(subscription.valid_until) : null}
            onChange={date => setSubscription({
              ...subscription,
              valid_until: date ? date.format('YYYY-MM-DD') : null,
            })}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Notes"
            variant="outlined"
            margin="dense"
            fullWidth
            multiline
            rows={2}
            disabled={isReadOnly}
            value={subscription.notes || ''}
            onChange={e => setSubscription({ ...subscription, notes: e.target.value })}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1" className={classes.planPricesTitle}>
            Price for each active plan
          </Typography>
          <Typography variant="caption" className={classes.planPricesHint} display="block">
            All active membership plans are listed here. Set this user&apos;s price for each plan. Another user can have different prices for the same plans. Leave a plan blank to hide it from this user in the app.
          </Typography>
          <Box>
            {planPriceRows.map(row => (
              <Box key={row.plan_id} className={classes.planPriceRow}>
                <Typography className={classes.planPriceName}>
                  {row.plan_name || 'Plan'}
                  {row.duration_days ? ` · ${row.duration_days} days` : ''}
                </Typography>
                <TextField
                  className={classes.planPriceField}
                  label="Amount (INR)"
                  type="number"
                  variant="outlined"
                  margin="dense"
                  disabled={isReadOnly}
                  value={row.amount == null ? '' : row.amount}
                  onChange={e => updatePlanPrice(row.plan_id, e.target.value)}
                />
              </Box>
            ))}
            {!plansLoaded && (
              <Typography variant="body2" color="textSecondary">
                Loading active membership plans…
              </Typography>
            )}
            {plansLoaded && plans.length === 0 && (
              <Typography variant="body2" color="textSecondary">
                No active membership plans. Add plans in Subscription Plans first.
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>

      {!isReadOnly && (
        <Grid container spacing={2} alignItems="center" className={classes.actionsRow}>
          <Grid item>
            <Button
              color="primary"
              variant="contained"
              className={classes.actionButton}
              onClick={handleSave}
            >
              Save Subscription
            </Button>
          </Grid>
          <Grid item>
            <Grid container spacing={1} alignItems="center" wrap="nowrap">
              <Grid item>
                <TextField
                  label="Extend days"
                  type="number"
                  variant="outlined"
                  margin="dense"
                  className={classes.extendField}
                  value={extendDays}
                  onChange={e => setExtendDays(e.target.value)}
                />
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  className={classes.actionButton}
                  onClick={handleExtend}
                >
                  Extend
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              className={classes.actionButton}
              onClick={() => {
                setOfflinePreview(null);
                setOfflineForm(prev => ({
                  ...prev,
                  coupon_code: '',
                  amount: currentPlanUserPrice(),
                }));
                setOfflineOpen(true);
              }}
            >
              Record Offline Payment
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              color="secondary"
              className={classes.actionButton}
              disabled={linkLoading || !currentPlanUserPrice() || parseFloat(currentPlanUserPrice()) <= 0}
              onClick={openPaymentLinkDialog}
            >
              {linkLoading ? 'Generating…' : 'Generate Razorpay Link'}
            </Button>
          </Grid>
        </Grid>
      )}

      <Box className={classes.historySection}>
        <Divider />
        <Typography variant="subtitle1" style={{ marginTop: 16, marginBottom: 8 }}>
          Membership history
        </Typography>
      {payments.length === 0 ? (
        <Typography variant="body2" color="textSecondary">No membership or payment records</Typography>
      ) : (
        <table className={classes.historyTable}>
          <thead>
            <tr>
              <th align="left">Paid</th>
              <th align="left">Mode</th>
              <th align="left">Amount</th>
              <th align="left">Coupon</th>
              <th align="left">Plan</th>
              <th align="left">Period start</th>
              <th align="left">Period end</th>
              <th align="left">Status</th>
              <th align="left">Invoice</th>
              <th align="left">Notes</th>
            </tr>
          </thead>
          <tbody>
            {payments.map(p => (
              <tr key={p.id} className={classes.historyRow}>
                <td>{p.paid_at ? moment(p.paid_at).format('DD/MM/YYYY') : '—'}</td>
                <td>{p.payment_mode || '—'}</td>
                <td>₹{p.amount}</td>
                <td>{p.coupon?.code || '—'}</td>
                <td>{p.plan?.name || '—'}</td>
                <td>{p.valid_from ? moment(p.valid_from).format('DD/MM/YYYY') : '—'}</td>
                <td>{p.valid_until ? moment(p.valid_until).format('DD/MM/YYYY') : '—'}</td>
                <td>{p.status}</td>
                <td>
                  {p.status === 'success' && Number(p.amount) > 0 ? (
                    <Button
                      color="primary"
                      size="small"
                      disabled={generatingInvoiceId === p.id}
                      onClick={() => handleGenerateInvoice(p)}
                    >
                      {generatingInvoiceId === p.id ? 'Generating…' : 'Download invoice'}
                    </Button>
                  ) : '—'}
                </td>
                <td>{p.notes || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      </Box>

      <Dialog open={offlineOpen} onClose={() => setOfflineOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Offline Payment</DialogTitle>
        <DialogContent>
          <TextField
            label="Amount (INR)"
            type="number"
            variant="outlined"
            margin="dense"
            fullWidth
            value={offlineForm.amount}
            onChange={e => {
              setOfflinePreview(null);
              setOfflineForm({ ...offlineForm, amount: e.target.value });
            }}
          />
          <Grid container spacing={1} alignItems="center">
            <Grid item xs>
              <TextField
                label="Coupon code"
                variant="outlined"
                margin="dense"
                fullWidth
                value={offlineForm.coupon_code}
                onChange={e => {
                  setOfflinePreview(null);
                  setOfflineForm({ ...offlineForm, coupon_code: e.target.value.toUpperCase() });
                }}
              />
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                onClick={() => applyCoupon({
                  code: offlineForm.coupon_code,
                  amount: offlineForm.amount,
                  setter: setOfflinePreview,
                })}
              >
                Apply
              </Button>
            </Grid>
          </Grid>
          {offlinePreview && (
            <Typography variant="body2" style={{ marginTop: 8, marginBottom: 8 }}>
              Original ₹{offlinePreview.original_amount} · Discount ₹{offlinePreview.discount_amount} · Payable ₹{offlinePreview.final_amount}
            </Typography>
          )}
          <TextField
            label="Extend days (optional override)"
            type="number"
            variant="outlined"
            margin="dense"
            fullWidth
            value={offlineForm.extend_days}
            onChange={e => setOfflineForm({ ...offlineForm, extend_days: e.target.value })}
          />
          <TextField
            label="Notes"
            variant="outlined"
            margin="dense"
            fullWidth
            multiline
            rows={2}
            value={offlineForm.notes}
            onChange={e => setOfflineForm({ ...offlineForm, notes: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOfflineOpen(false)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={handleOfflinePayment}>
            Record Payment
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={linkOpen} onClose={() => setLinkOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Razorpay Payment Link</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Share this link with the user to collect ₹{linkChargedAmount != null ? linkChargedAmount : currentPlanUserPrice()}. Subscription extends automatically after payment (via webhook).
          </Typography>
          <TextField
            className={classes.paymentLinkField}
            label="Payment link"
            variant="outlined"
            margin="dense"
            fullWidth
            value={paymentLink}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Copy link">
                    <IconButton onClick={handleCopyLink} edge="end">
                      <FileCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Open link">
                    <IconButton onClick={handleOpenLink} edge="end">
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          <Typography variant="caption" className={classes.linkHint} display="block">
            Save subscription first. Requires Razorpay keys in backend .env and webhook event <strong>payment_link.paid</strong> enabled.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkOpen(false)}>Close</Button>
          <Button color="primary" variant="contained" startIcon={<LinkIcon />} onClick={handleCopyLink}>
            Copy Link
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={linkGenerateOpen} onClose={() => !linkLoading && setLinkGenerateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Razorpay Link</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Original amount ₹{currentPlanUserPrice() || 0}. Apply a coupon before generating the link.
          </Typography>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs>
              <TextField
                label="Coupon code"
                variant="outlined"
                margin="dense"
                fullWidth
                value={linkForm.coupon_code}
                onChange={e => {
                  setLinkPreview(null);
                  setLinkForm({ coupon_code: e.target.value.toUpperCase() });
                }}
              />
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                onClick={() => applyCoupon({
                  code: linkForm.coupon_code,
                  amount: currentPlanUserPrice(),
                  setter: setLinkPreview,
                })}
              >
                Apply
              </Button>
            </Grid>
          </Grid>
          {linkPreview && (
            <Typography variant="body2" style={{ marginTop: 8 }}>
              Original ₹{linkPreview.original_amount} · Discount ₹{linkPreview.discount_amount} · Payable ₹{linkPreview.final_amount}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkGenerateOpen(false)} disabled={linkLoading}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={handleGeneratePaymentLink} disabled={linkLoading}>
            {linkLoading ? 'Generating…' : 'Generate Link'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

SubscriptionDetails.propTypes = {
  userid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  viewtype: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default SubscriptionDetails;
